import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { BOX_IMAGES } from '@/data/images';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { resolveImageUrl } from '@/components/admin/ImageUploader';
import { API } from '@/lib/api';

const EASE = [0.22, 1, 0.36, 1];

const DEFAULT_GALLERY = [
  { id: 'd1', image_url: BOX_IMAGES.kettlebellWoman, alt: 'Entraînement kettlebell - CrossFit Saint-Chamond', label: 'KETTLEBELL', size: 'tall' },
  { id: 'd2', image_url: BOX_IMAGES.tireFlip, alt: 'Tire flip - puissance et conditioning CrossFit', label: 'POWER', size: 'square' },
  { id: 'd3', image_url: BOX_IMAGES.highFive, alt: "Communauté CrossFit - esprit d'équipe Saint-Chamond", label: 'TEAM', size: 'wide' },
  { id: 'd4', image_url: BOX_IMAGES.dbSnatch, alt: 'Dumbbell snatch - mouvement fonctionnel', label: 'SNATCH', size: 'square' },
  { id: 'd5', image_url: BOX_IMAGES.deadlift, alt: 'Soulevé de terre - force à la box', label: 'FORCE', size: 'tall' },
  { id: 'd6', image_url: BOX_IMAGES.rings, alt: 'Anneaux gymniques - gymnastique CrossFit', label: 'GYMNASTIC', size: 'square' },
];

const sizeClass = {
  tall: 'row-span-2 aspect-[3/5]',
  square: 'aspect-square',
  wide: 'aspect-[4/3] md:col-span-2',
};

export const Gallery = () => {
  const [items, setItems] = useState(DEFAULT_GALLERY);

  useEffect(() => {
    axios.get(`${API}/content/gallery`).then((r) => {
      if (Array.isArray(r.data) && r.data.length > 0) setItems(r.data);
    }).catch(() => {});
  }, []);

  return (
    <section
      data-testid="gallery-section"
      className="relative bg-background overflow-hidden"
    >
      <div className="absolute -top-20 right-0 w-[400px] h-[400px] bg-primary/5 rounded-full blur-[120px] pointer-events-none" />

      <div className="container-custom relative z-10 section-padding">
        <div className="grid md:grid-cols-5 gap-6 items-end mb-14">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-10%' }}
            transition={{ duration: 0.8, ease: EASE }}
            className="md:col-span-3"
          >
            <span className="text-primary font-semibold text-xs tracking-[0.3em] uppercase">
              ━━ La box en images
            </span>
            <h2 className="font-heading text-4xl sm:text-5xl md:text-6xl text-dark mt-4 leading-[0.95]">
              L'AMBIANCE DE NOTRE{' '}
              <span className="text-gradient-primary italic font-display inline-block pr-2">
                BOX
              </span>
            </h2>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-10%' }}
            transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
            className="md:col-span-2"
          >
            <p className="text-muted-foreground text-base leading-relaxed mb-5">
              Découvre l'énergie de nos cours, la diversité des mouvements et
              l'esprit collectif qui fait vibrer CrossFit Saint-Chamond.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 text-primary font-heading text-sm tracking-wider group link-underline"
            >
              VIENS ESSAYER
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </motion.div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 auto-rows-[180px] md:auto-rows-[220px]">
          {items.map((item, idx) => (
            <motion.div
              key={item.id || idx}
              initial={{ opacity: 0, scale: 0.92, y: 20 }}
              whileInView={{ opacity: 1, scale: 1, y: 0 }}
              viewport={{ once: true, margin: '-5%' }}
              transition={{ duration: 0.7, delay: idx * 0.08, ease: EASE }}
              className={`group relative overflow-hidden rounded-2xl ${sizeClass[item.size] || 'aspect-square'} bg-dark/10`}
            >
              <img decoding="async"
                src={resolveImageUrl(item.image_url)}
                alt={item.alt || ''}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-dark/85 via-dark/10 to-transparent opacity-80 group-hover:opacity-100 transition-opacity duration-500" />
              {item.label && (
                <div className="absolute bottom-3 left-3 md:bottom-4 md:left-4 flex items-center gap-2">
                  <span className="h-px w-6 bg-primary-glow" />
                  <span className="text-white/90 font-heading text-xs tracking-[0.25em]">
                    {item.label}
                  </span>
                </div>
              )}
              <div className="absolute inset-0 rounded-2xl ring-0 ring-primary-glow/0 group-hover:ring-2 group-hover:ring-primary-glow/40 transition-all duration-500" />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
